import React, { useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import * as S from './styles'

import { cpfMask, phoneMask } from 'presentation/utils/masks'
import PasswordField from 'presentation/shared/components/PasswordField'
import Heading from 'presentation/shared/components/Heading'
import Button from 'presentation/shared/components/Button'
import Carousel, {
  CarouselState
} from 'presentation/shared/components/Carousel'
import Checkbox from 'presentation/shared/components/Checkbox'
import 'main/config/yup'
import { toast } from 'react-toastify'
import Modal from 'presentation/shared/components/Modal'
import { useHistory } from 'react-router'
import { FinishCollaboratorSignup } from 'domain/usecases/collaborator/finish-collaborator-signup'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import HeadingNew from 'presentation/shared/components/HeadingNew'

type HospitalSignupFormProps = {
  initialValues?: HospitalSignupFormValues
  finishCollaboratorSignup?: FinishCollaboratorSignup
  token?: string
}

export default function HospitalSignupForm({
  initialValues = {} as HospitalSignupFormValues,
  finishCollaboratorSignup,
  token
}: HospitalSignupFormProps) {
  const history = useHistory()
  const [carousel, setCarousel] = useState({} as CarouselState)
  const [handleSuccessModal, setHandleSuccessModal] = useState(false)

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      try {
        await finishCollaboratorSignup?.add({
          cpf: values.cpf.replace(/\D+/g, ''),
          inviteToken: token ?? '',
          password: values.password ?? '',
          phone: values.phone.replace(/\D+/g, '')
        })
        setHandleSuccessModal(true)
      } catch (error: any) {
        return toast.error(error.message)
      }
    }
  })

  return (
    <S.Wrapper role="form" onSubmit={formik.handleSubmit}>
      <Carousel
        touch={false}
        slidesPerView={1}
        state={carousel}
        setState={setCarousel}
      >
        <>
          <HeadingNew as="h1" size="xlarge">
            Bem-vindo(a) ao <strong>Meu Mater Dei</strong>
          </HeadingNew>
          <SupportTextNew className="paragraph">
            Acesse a plataforma para acompanhar seus processos e procedimentos
            na Rede Mater Dei.
          </SupportTextNew>
          <S.Inputs>
            <TextFieldNew
              label="Nome"
              placeholder="Seu Nome"
              name="name"
              defaultValue={formik.values.name}
              onInputChange={formik.handleChange('name')}
              onBlur={formik.handleBlur('name')}
              error={formik.errors.name}
            />
            <TextFieldNew
              label="CPF"
              name="cpf"
              placeholder="000.000.000-00"
              mask={cpfMask}
              value={formik.values.cpf ?? ''}
              onInputChange={formik.handleChange('cpf')}
              onBlur={formik.handleBlur('cpf')}
              error={formik.errors.cpf}
            />
            <TextFieldNew
              label="Celular"
              name="phone"
              placeholder="+55 11 90000-0000"
              mask={phoneMask}
              value={formik.values.phone ?? ''}
              onInputChange={formik.handleChange('phone')}
              onBlur={formik.handleBlur('phone')}
              error={formik.errors.phone}
            />
            <TextFieldNew
              label="E-mail"
              placeholder="seu@email.com"
              name="email"
              value={formik.values.email}
              onInputChange={formik.handleChange('email')}
              onBlur={formik.handleBlur('email')}
              error={formik.errors.email}
            />
            <PasswordField
              label="Senha"
              placeholder="••••••••"
              name="password"
              initialValue={formik.values.password}
              onInputChange={formik.handleChange('password')}
              onBlur={formik.handleBlur('password')}
              error={formik.errors.password}
            />
            <PasswordField
              label="Confirmar senha"
              placeholder="••••••••"
              name="passwordConfirmation"
              initialValue={formik.values.passwordConfirmation}
              onInputChange={formik.handleChange('passwordConfirmation')}
              onBlur={formik.handleBlur('passwordConfirmation')}
              error={formik.errors.passwordConfirmation}
            />
          </S.Inputs>
          <Button
            disabled={!formik.isValid || formik.isSubmitting}
            type="button"
            onClick={carousel.slideNext}
          >
            Confirmar
          </Button>
        </>
        <>
          <Heading as="h1" size="large" style={{ paddingBottom: '61px' }}>
            Termo de confidencialidade
          </Heading>
          <iframe
            src="https://production-exp-paciente-public-assets.s3.amazonaws.com/terms/Termo+de+confidencialidade.pdf"
            style={{
              width: '100%',
              height: '360px',
              border: 'none',
              paddingBottom: '24px'
            }}
          />
          <Checkbox
            label="Aceito o termo de confidencialidade"
            name="terms"
            labelFor="aceito o termo de confidencialidade"
            isChecked={formik.values.terms}
            onCheck={() => formik.setFieldValue('terms', !formik.values.terms)}
          />
          <Button
            disabled={
              !formik.isValid || !formik.values.terms || formik.isSubmitting
            }
            type="submit"
            style={{ marginTop: '24px' }}
          >
            Continuar
          </Button>
        </>
      </Carousel>
      <Modal
        title="Senha definida com sucesso!"
        show={handleSuccessModal}
        close={() => history.push('/login')}
      />
    </S.Wrapper>
  )
}

export type HospitalSignupFormValues = {
  name: string
  cpf: string
  phone: string
  email: string
  password?: string
  passwordConfirmation?: string
  terms?: boolean
}

const validationSchema = yup.object().shape({
  name: yup.string().required(),
  cpf: yup.string().cpf().required(),
  // Paliativo
  phone: yup
    .string()
    .test('phone-format', 'Telefone inválido.', function (val) {
      if (!val) return false

      // Remove todos os caracteres não numéricos para contar dígitos
      const digitsOnly = val.replace(/\D/g, '')

      // Deve ter exatamente 13 dígitos (55 + 2 DDD + 9 número)
      if (digitsOnly.length !== 13) return false

      // Deve começar com 55 (código do Brasil)
      if (!digitsOnly.startsWith('55')) return false

      // O terceiro dígito do número (após DDD) deve ser 9 (celular)
      const phoneNumber = digitsOnly.substring(4) // Remove 55 + DDD
      if (!phoneNumber.startsWith('9')) return false

      // Aceita os seguintes formatos:
      // 1. Com máscara completa: +55 XX XXXXX-XXXX
      // 2. Sem espaços/hífen: +55XXXXXXXXXXX ou 55XXXXXXXXXXX
      const withMaskFormat = /^\+55\s\d{2}\s\d{5}-\d{4}$/
      const withoutSpacesFormat = /^\+?55\d{2}\d{9}$/

      return withMaskFormat.test(val) || withoutSpacesFormat.test(val)
    })
    .required(),
  email: yup.string().email().required(),
  password: yup.string().required(),
  passwordConfirmation: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Confirmação diferente da senha')
    .required()
})
